import * as Yup from 'yup';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { styled } from '@mui/material/styles';
import { Link, Stack, Typography, TextField, IconButton, InputAdornment, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotificationsProvider } from '../../context/NotificationsContext';
// component
import Iconify from '../../components/Iconify';
import Logo from '../../components/Logo';

import * as CognitoAuthService from '../../services/CognitoAuthService';

// ----------------------------------------------------------------------
const LoginStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

const ContainerStyle = styled(Box)(({ theme }) => ({
  shadowColor: theme.palette.secondary,
  backgroundColor: theme.palette.grey,
  shadowOpacity: 0.8,
  shadowRadius: 2,
  shadowOffset: { height: 1, width: 1 },
  elevation: 5,
}));

const LogoStyle = { width: 175, height: 48 };

// Two-step Cognito forgot-password flow:
//   1. user enters email -> CognitoAuthService.forgotPassword sends a 6-digit
//      code to the verified email (rendered by our customMessage Lambda).
//   2. user enters code + new password -> CognitoAuthService.confirmPassword
//      finishes the reset; we redirect to /login.
export default function ForgotPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();

  const [step, setStep] = useState(1);
  const [requestedEmail, setRequestedEmail] = useState('');

  const RequestSchema = Yup.object().shape({
    email: Yup.string().email(t('forgot-password.invalid-email')).required(t('forgot-password.required-email')),
  });

  const requestFormik = useFormik({
    initialValues: { email: '' },
    validationSchema: RequestSchema,
    onSubmit: async (data, { setSubmitting }) => {
      try {
        await CognitoAuthService.forgotPassword(data.email);
        setRequestedEmail(data.email);
        setStep(2);
        showNotification('success', t('forgot-password.email-sent'));
      } catch (err) {
        showNotification('error', err?.message || t('forgot-password.email-not-sent'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const ConfirmSchema = Yup.object().shape({
    code: Yup.string()
      .trim()
      .matches(/^\d{6}$/, t('forgot-password.invalid-code', 'Code must be 6 digits'))
      .required(t('forgot-password.required-code', 'Verification code is required')),
    password: Yup.string()
      .min(12, t('forgot-password.password-too-short', 'Password must be at least 12 characters'))
      .matches(/[A-Z]/, t('forgot-password.password-missing-uppercase', 'Must contain at least one uppercase letter'))
      .matches(/[a-z]/, t('forgot-password.password-missing-lowercase', 'Must contain at least one lowercase letter'))
      .matches(/[0-9]/, t('forgot-password.password-missing-number', 'Must contain at least one number'))
      .matches(/[^A-Za-z0-9]/, t('forgot-password.password-missing-symbol', 'Must contain at least one special character'))
      .required(t('forgot-password.required-password', 'New password is required')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('forgot-password.passwords-must-match', 'Passwords must match'))
      .required(t('forgot-password.required-confirm-password', 'Please confirm your password')),
  });

  const [showPassword, setShowPassword] = useState(false);

  const confirmFormik = useFormik({
    initialValues: { code: '', password: '', confirmPassword: '' },
    validationSchema: ConfirmSchema,
    onSubmit: async (data, { setSubmitting }) => {
      try {
        await CognitoAuthService.confirmPassword(requestedEmail, data.code.trim(), data.password);
        showNotification('success', t('forgot-password.reset-success', 'Password reset. You can now sign in.'));
        navigate('/login');
      } catch (err) {
        showNotification('error', err?.message || t('forgot-password.reset-failed', 'Could not reset password'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (step === 2) {
    const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = confirmFormik;
    return (
      <LoginStyle>
        <ContainerStyle>
          <FormikProvider value={confirmFormik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Stack alignItems="center">
                  <Logo sx={LogoStyle} />
                </Stack>
                <Typography variant="h4">
                  {t('forgot-password.confirm-title', 'Enter the verification code')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('forgot-password.confirm-description', 'A 6-digit code was sent to {{email}}. Enter it together with the new password.', { email: requestedEmail })}
                </Typography>

                <TextField
                  fullWidth
                  autoComplete="one-time-code"
                  label={t('forgot-password.code-label', 'Verification code')}
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' },
                  }}
                  {...getFieldProps('code')}
                  error={Boolean(touched.code && errors.code)}
                  helperText={touched.code && errors.code}
                />

                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'}
                  label={t('forgot-password.new-password-label', 'New password')}
                  {...getFieldProps('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password ? errors.password : t('forgot-password.password-requirements', 'Must be at least 12 characters with uppercase, lowercase, number, and symbol')}
                />

                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'}
                  label={t('forgot-password.confirm-password-label', 'Confirm new password')}
                  {...getFieldProps('confirmPassword')}
                  error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                />

                <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                  <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
                    {t('forgot-password.reset-button', 'Reset password')}
                  </LoadingButton>
                  <Link
                    component="button"
                    type="button"
                    variant="subtitle2"
                    underline="hover"
                    onClick={() => {
                      setStep(1);
                      confirmFormik.resetForm();
                    }}
                  >
                    {t('forgot-password.use-different-email', 'Use a different email')}
                  </Link>
                  <Link component={RouterLink} variant="subtitle2" to="/login" underline="hover">
                    {t('forgot-password.back-to-login', 'Back to login')}
                  </Link>
                </Stack>
              </Stack>
            </Form>
          </FormikProvider>
        </ContainerStyle>
      </LoginStyle>
    );
  }

  // Step 1 — request the code
  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = requestFormik;
  return (
    <LoginStyle>
      <ContainerStyle>
        <FormikProvider value={requestFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={10}>
              <Stack spacing={3}>
                <Stack alignItems="center">
                  <Logo sx={LogoStyle} />
                </Stack>
                <Typography variant="h4">
                  {t('forgot-password.title')}
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="username"
                  type="email"
                  label={t('forgot-password.email-label')}
                  {...getFieldProps('email')}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Stack>
              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
                  {t('forgot-password.send-button')}
                </LoadingButton>
                <Link component={RouterLink} variant="subtitle2" to="/login" underline="hover">
                  {t('forgot-password.back-to-login', 'Back to login')}
                </Link>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </ContainerStyle>
    </LoginStyle>
  );
}
